import { useSyncExternalStore } from "react";

export const KAMELEOON_FLAGS = {
  buyboxReco: "buyboxReco",
  preferredProductsDesign: "preferredProductsDesign",
  campaigndiscount: "campaigndiscount",
  cpdBannerDrawer: "cpdBannerDrawer",
  cpdBannerCart: "cpdBannerCart",
  comparisonAvailability: "comparisonAvailability",
  searchAvailability: "searchAvailability",
  recoAvailability: "recoAvailability",
  recoPrice: "recoPrice",
  bsaAvailability: "bsaAvailability",
  relatedProductPDPAvailability: "relatedProductPDPAvailability",
  zoeSRP: "zoeSRP",
} as const;

export type KameleoonFlagName = keyof typeof KAMELEOON_FLAGS;

export interface KameleoonFlagValues {
  buyboxReco: boolean;
  preferredProductsDesign: "A" | "B" | undefined;
  campaigndiscount: boolean;
  cpdBannerDrawer: boolean;
  cpdBannerCart: boolean;
  comparisonAvailability: boolean;
  searchAvailability: boolean;
  recoAvailability: boolean;
  recoPrice: boolean;
  bsaAvailability: boolean;
  relatedProductPDPAvailability: boolean;
  zoeSRP: boolean;
}

export const KAMELEOON_FLAG_DEFAULTS: Readonly<KameleoonFlagValues> = Object.freeze({
  buyboxReco: false,
  preferredProductsDesign: undefined,
  campaigndiscount: false,
  cpdBannerDrawer: false,
  cpdBannerCart: false,
  comparisonAvailability: false,
  searchAvailability: false,
  recoAvailability: false,
  recoPrice: false,
  bsaAvailability: false,
  relatedProductPDPAvailability: false,
  zoeSRP: false,
});

type FlagSource = Record<string, unknown> | undefined;
type FlagLookup<Name extends KameleoonFlagName> = { found: true; value: KameleoonFlagValues[Name] } | { found: false };

const listeners = new Set<() => void>();
const hasOwn = (source: object, name: string): boolean => Object.prototype.hasOwnProperty.call(source, name);

function getBrowserWindow(): Window | undefined {
  return typeof window === "undefined" ? undefined : window;
}

function isValidFlagValue<Name extends KameleoonFlagName>(
  name: Name,
  value: unknown,
): value is KameleoonFlagValues[Name] {
  if (name === KAMELEOON_FLAGS.preferredProductsDesign) {
    return value === undefined || value === "A" || value === "B";
  }

  return typeof value === "boolean";
}

function readFlag<Name extends KameleoonFlagName>(source: FlagSource, name: Name): FlagLookup<Name> {
  if (!source || !hasOwn(source, name)) {
    return { found: false };
  }

  const value = source[name];
  if (isValidFlagValue(name, value)) {
    return { found: true, value };
  }

  console.warn(`Ignoring invalid Kameleoon flag value for "${name}".`);
  return { found: false };
}

export function isKameleoonFlagName(name: string): name is KameleoonFlagName {
  return Object.values(KAMELEOON_FLAGS).includes(name as (typeof KAMELEOON_FLAGS)[KameleoonFlagName]);
}

export function getKameleoonFlag<Name extends KameleoonFlagName>(name: Name): KameleoonFlagValues[Name] {
  const browserWindow = getBrowserWindow();
  if (browserWindow) {
    const kameleoonValue = readFlag(browserWindow.kameleoonFlags as FlagSource, name);
    if (kameleoonValue.found) {
      return kameleoonValue.value;
    }

    const legacyValue = readFlag(browserWindow.SCHA?.feature as FlagSource, name);
    if (legacyValue.found) {
      return legacyValue.value;
    }
  }

  return KAMELEOON_FLAG_DEFAULTS[name];
}

export function useKameleoonFlag<Name extends KameleoonFlagName>(name: Name): KameleoonFlagValues[Name] {
  return useSyncExternalStore(
    (onStoreChange) => {
      listeners.add(onStoreChange);
      return () => listeners.delete(onStoreChange);
    },
    () => getKameleoonFlag(name),
    () => KAMELEOON_FLAG_DEFAULTS[name],
  );
}

export function setKameleoonFlag<Name extends KameleoonFlagName>(
  name: Name,
  value: KameleoonFlagValues[Name],
): boolean {
  if (!isKameleoonFlagName(name) || !isValidFlagValue(name, value)) {
    console.warn(`Ignoring invalid Kameleoon flag assignment for "${String(name)}".`);
    return false;
  }

  const browserWindow = getBrowserWindow();
  if (!browserWindow) {
    return false;
  }

  browserWindow.kameleoonFlags ??= {};
  browserWindow.kameleoonFlags[name] = value;
  listeners.forEach((listener) => listener());
  return true;
}

declare global {
  interface Window {
    kameleoonFlags?: Partial<KameleoonFlagValues>;
    setKameleoonFlag?: typeof setKameleoonFlag;
  }
}

const browserWindow = getBrowserWindow();
if (browserWindow) {
  browserWindow.setKameleoonFlag = setKameleoonFlag;
}
